// Caminho: vita-frontend/src/pages/Employee/ProfilePage/index.js

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAuth } from "../../../contexts/AuthContext";
import {
    requestUserChanges,
    updateUser,
} from "../../../services/usersService";
import { resetEmailPassword } from "../../../services/userService";
import _ from "lodash";
import {
    formatDocument,
    formatPhoneNumber, maskCNPJ, maskCPF, maskPhone,
    unmask,
} from "../../../utils/usersUtils";

// --- Styled Components (Preservados e adaptados) ---
const ProfileWrapper = styled.div`
    max-width: 800px;
    margin: 24px auto;
    padding: 24px;
`;

const FormSection = styled.form`
    background-color: #fff;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    margin-bottom: 32px;
`;

const Title = styled.h1`
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 32px;
`;

const Form = styled.form`
    background-color: #fff;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const Section = styled.div`
    margin-bottom: 24px;
    border-bottom: 1px solid #eee;
    padding-bottom: 24px;
`;

const SectionTitle = styled.h2`
    font-size: 20px;
    color: #333;
    margin-bottom: 16px;
`;

const FormGroup = styled.div`
    margin-bottom: 16px;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 8px;
    font-weight: bold;
`;

const Input = styled.input`
    width: 100%;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    &:disabled {
        background-color: #f1f1f1;
    }
`;

const Button = styled.button`
    padding: 12px 24px;
    border: none;
    border-radius: 4px;
    font-weight: bold;
    cursor: pointer;
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    &:disabled {
        background-color: #ccc;
    }
`;


const ProfilePage = () => {
    const { user, loading, logout } = useAuth(); // Adicionado logout

    const [userForm, setUserForm] = useState({});
    const [initialUserData, setInitialUserData] = useState({});
    const [accountForm, setAccountForm] = useState({
        email: "",
        password: "",
        confirmPassword: "",
    });
    const [message, setMessage] = useState("");
    const [formError, setFormError] = useState("");



    useEffect(() => {
        if (user) {
            const userData = {
                phoneNumber: formatPhoneNumber(user.phoneNumber) || "",
                name: user.name || "",
                cpf: formatDocument(user.cpf) || "",
                tradeName: user.tradeName || "",
                companyName: user.companyName || "",
                cnpj: formatDocument(user.cnpj) || "",
                userType: user.userType || "",
            };
            setUserForm(userData);
            setInitialUserData(userData);
            setAccountForm((prev) => ({ ...prev, email: user.email || "" }));
        }
    }, [user]);

    const handleUserChange = (e) => {
        const { name, value } = e.target;
        // Aplica a máscara correspondente ao campo
        let maskedValue = value;
        if (name === "cpf") {
            maskedValue = maskCPF(value);
        } else if (name === "cnpj") {
            maskedValue = maskCNPJ(value);
        } else if (name === "phoneNumber") {
            maskedValue = maskPhone(value);
        }
        setUserForm({ ...userForm, [e.target.name]: maskedValue });
    };

    const handleAccountChange = (e) => {
        setAccountForm({ ...accountForm, [e.target.name]: e.target.value });
    };

    

    const handleUserSubmit = async (e) => {
        e.preventDefault();

        const changesPayload = Object.keys(userForm)
            .filter(
                (key) => !_.isEqual(userForm[key], initialUserData[key])
            )
            .reduce((acc, key) => {
                acc[key] = unmask(userForm[key]);
                return acc;
            }, {});

        if (Object.keys(changesPayload).length === 0) {
            alert("Nenhuma alteração nos dados pessoais foi feita.");
            return;
        }

        try {
            if (user.userType === "A") {
                changesPayload.userType = user.userType;
                await updateUser(user.currentUserID, changesPayload);
                setInitialUserData(userForm);
                window.location.reload();
            } else {
                if(changesPayload.phoneNumber)
                    changesPayload.phoneNumber=maskPhone(changesPayload.phoneNumber);
                if(changesPayload.cpf)
                    changesPayload.cpf=maskCPF(changesPayload.cpf);
                if(changesPayload.cnpj)
                    changesPayload.cnpj=maskCNPJ(changesPayload.cnpj);
                await requestUserChanges(changesPayload);

                setInitialUserData(userForm);
                alert("Solicitação enviada com sucesso");
            }
        } catch (error) {
            alert("Falha ao atualizar dados pessoais.");
        }
    };

    const handleAccountSubmit = async (e) => {
        e.preventDefault();

        setMessage(""); setFormError("");
        if (accountForm.password.length < 6) {
            setFormError("A senha deve ter no mínimo 6 caracteres.");
            return;
        }


        if (accountForm.password !== accountForm.confirmPassword) {
            setFormError("As senhas não coincidem.");
            return;
        }

        const payload = {};

        payload.email = user.email;
        payload.userType=userForm.userType;
        if (accountForm.email !== user.email) payload.email = accountForm.email;
        if (accountForm.password) payload.password = accountForm.password;

        if (Object.keys(payload).length === 0) {
            setFormError("Nenhuma alteração nos dados da conta foi feita.");
            return;
        }

        try {
            await resetEmailPassword(user.id, payload);
            alert(
                "Dados da conta atualizados com sucesso! Por segurança, você será deslogado."
            );
            logout(); // Desloga o usuário após a mudança
        } catch (error) {
            console.log(error.message);
            setFormError("Falha ao atualizar dados da conta.");
            return;
        }
    };

    if (loading || !user) return <div>Carregando perfil...</div>;

    return (
        <ProfileWrapper>
            <Title>Meu Perfil</Title>

            <FormSection onSubmit={handleUserSubmit}>
                <SectionTitle>Informações Pessoais</SectionTitle>
                {user.userType !== "A" && (
                    <p
                        style={{
                            fontSize: "14px",
                            color: "#666",
                            marginTop: "-10px",
                            marginBottom: "20px",
                        }}
                    >
                        Qualquer alteração aqui será enviada para aprovação de
                        um administrador.
                    </p>
                )}
                <FormGroup>
                    <Label>Telefone</Label>
                    <Input
                        type="text"
                        name="phoneNumber"
                        value={userForm.phoneNumber || ""}
                        onChange={handleUserChange}
                        pattern="(\(?\d{2}\)?\s?\d{4,5}-?\d{4})|(0\d{3}-\d{3}-\d{4})"
                        title="Digite um telefone válido (ex: (15) 99611-0650 ou 0800-727-1100)"
                    />
                </FormGroup>
                {(user.userType === "PF" ||
                    user.userType === "F" ||
                    user.userType === "A") && (
                    <FormGroup>
                        <Label>Nome Completo</Label>
                        <Input
                            type="text"
                            name="name"
                            value={userForm.name || ""}
                            onChange={handleUserChange}
                        />
                    </FormGroup>
                )}
                {user.userType === "PF" && (
                    <FormGroup>
                        <Label>CPF</Label>
                        <Input
                            type="text"
                            name="cpf"
                            value={userForm.cpf || ""}
                            onChange={handleUserChange}
                            pattern="\d{3}\.?\d{3}\.?\d{3}-?\d{2}"   // regex for CPF format
                            title="CPF deve ter 11 dígitos (ex: 123.456.789-00)"
                        />
                    </FormGroup>
                )}
                {user.userType === "PJ" && (
                    <>
                        <FormGroup>
                            <Label>Nome Fantasia</Label>
                            <Input
                                type="text"
                                name="tradeName"
                                value={userForm.tradeName || ""}
                                onChange={handleUserChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Razão Social</Label>
                            <Input
                                type="text"
                                name="companyName"
                                value={userForm.companyName || ""}
                                onChange={handleUserChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>CNPJ</Label>
                            <Input
                                type="text"
                                name="cnpj"
                                value={userForm.cnpj || ""}
                                onChange={handleUserChange}
                                pattern="\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}"   // regex for CPF format
                                title="CNPJ deve ter 14 dígitos (ex: 12.345.678/0001-99)"
                            />
                        </FormGroup>
                    </>
                )}
                <Button type="submit">
                    {user.userType === "A"
                        ? "Salvar Dados Pessoais"
                        : "Solicitar Alterações"}
                </Button>
            </FormSection>

            <FormSection onSubmit={handleAccountSubmit}>
                <SectionTitle>Informações da Conta</SectionTitle>
                <FormGroup>
                    <Label>Email</Label>
                    <Input
                        type="email"
                        name="email"
                        value={accountForm.email}
                        onChange={handleAccountChange}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Nova Senha (deixe em branco para não alterar)</Label>
                    <Input
                        type="password"
                        name="password"
                        value={accountForm.password}
                        onChange={handleAccountChange}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Confirmar Nova Senha</Label>
                    <Input
                        type="password"
                        name="confirmPassword"
                        value={accountForm.confirmPassword}
                        onChange={handleAccountChange}
                    />
                </FormGroup>
                <Button type="submit">Salvar Dados da Conta</Button>
                {formError && ( <p style={{ color: "red", textAlign: "center", marginTop: "24px"}}>{formError}</p>)}
                {message && <p style={{ color: "green", textAlign: "center", marginTop: "24px"}}>{message}</p>}
            </FormSection>
        </ProfileWrapper>
    );
};

export default ProfilePage;
